import {Badge, Table} from 'antd';
import {ColumnType, TableProps,ColumnGroupType} from 'antd/es/table';
import {SortOrder} from 'antd/es/table/interface';
import cn from 'classnames';
import React, {
    ReactElement, useMemo,
} from 'react';

import {IconsMap} from 'components/dynamic-icon';
import {EFilterValueType} from 'features/predicate-expression-form-list';
import {ITableAttributes} from 'shared/types';
import {TStateSetter} from 'shared/types/generics';
import {ILookupEntry} from 'shared/types/lookups';
import {ISortQuery} from 'shared/types/requests';
import {TAppState} from 'store/config/types';

import {TableColumnFilter} from './table-column-filter';
import {useTableColumnFilters, TTableColumnFilters} from './table-column-filter/use-table-column-filter';
import {IColumnSortData, TableColumnMenu, useTableColumnSort} from './table-column-menu';

import './custom-table.less';

export type TCustomColumn<RecordType extends object = any> = (ColumnGroupType<RecordType> | ColumnType<RecordType>) & {
    withCustomFilters?: boolean;
    hideSortDropdown?: boolean;
    withCustomSort?: boolean;
    withHideColumnButton?: boolean;
    dataIndex?: string;
}

export interface IUseCustomTableArgs<RecordType extends object = any> extends Omit<TableProps<RecordType>, 'columns'> {
    onColumn?: <T extends TCustomColumn<RecordType>>(column: T, index: number) => T;
    columns?: TCustomColumn<RecordType>[];

    tableFiltersData?: TTableColumnFilters;
    setTableFiltersData?: (data: TTableColumnFilters | undefined) => void;

    tableColumnSortData?: IColumnSortData;
    setTableColumnSortData?: TStateSetter<IColumnSortData>;

    selectColumnFilterEntriesByFieldType?: (state: TAppState, arg: {
      fieldType: EFilterValueType;
    }) => ILookupEntry[] | undefined;
    tableAttributes?: ITableAttributes[];

    isSingleColumnSort?: boolean;
}

interface ICustomTableProps<RecordType extends object = any> extends Omit<TableProps<RecordType>, 'columns'> {
    onColumn?: <T extends ColumnType<RecordType>>(column: T, index: number) => T;
    columns?: ColumnType<RecordType>[];
}

export const CustomTable = <RecordType extends object = any, >(
    props: ICustomTableProps<RecordType>,
): ReactElement => {
    const {
        className, children, onColumn, columns, ...restProps
    } = props;

    const modifiedColumns = useMemo(() => columns?.map((col, index) => {
        if (!onColumn) return col;
        return onColumn(col, index);
    }), [columns]);

    return (
        <Table
            className={cn('custom-table', className)}
            columns={modifiedColumns}
            {...restProps}
        >
            {children}
        </Table>
    );
};

export const useCustomTable = <RecordType extends object = any, >(args: IUseCustomTableArgs<RecordType>) => {
    const {
        columns,
        onColumn,
        setTableColumnSortData = () => {},
        tableColumnSortData = {filters: {}, sort: {}, noFetch: false},
        tableFiltersData,
        setTableFiltersData,
        selectColumnFilterEntriesByFieldType,
        tableAttributes,
        isSingleColumnSort = false,
        ...restProps
    } = args;

    const {
        createTableColumnFilterDataGetter,
        createTableColumnFilterDataSetter,
        getTableColumnFilterInfo,
    } = useTableColumnFilters({
        tableFiltersData,
        setTableFiltersData,
    });

    const {
        tableColumnSortDataAdaptedForRequest,
        createSortDataGetter,
        createSortDataSetter,
    } = useTableColumnSort({
        tableColumnSortData,
        isSingleColumnSort,
        setTableColumnSortData({sort}) {
            setTableColumnSortData({
                sort,
                noFetch: false,
            });
        },
    });

    const modifiedColumns = useMemo(() => columns?.map((col, index) => {
        if (!onColumn) return col;
        return onColumn(col, index);
    }), [columns]);

    const columnsWithFilters = modifiedColumns?.map(col => {
        if (col.withCustomFilters || col.withCustomSort) {
            return {
                ...col,
                title: (
                    <div className="d-flex align-items-center">
                        {col.title}
                        {!col.withCustomSort && (() => {
                            const {dataIndex} = col;

                            if (!dataIndex
                              || !tableAttributes
                              || !selectColumnFilterEntriesByFieldType) return undefined;

                            const filterInfo = getTableColumnFilterInfo(dataIndex);

                            return (
                                <Badge
                                    count={filterInfo.rulesCount}
                                    size="small"
                                >
                                    <TableColumnFilter
                                        columnName={dataIndex}
                                        getTableColumnFilterData={createTableColumnFilterDataGetter(dataIndex)}
                                        setTableColumnFilterData={createTableColumnFilterDataSetter(dataIndex)}
                                        isApplied={filterInfo.isApplied}
                                        selectColumnFilterEntriesByFieldType={selectColumnFilterEntriesByFieldType}
                                        tableAttributes={tableAttributes}
                                    />
                                </Badge>
                            );
                        })()}
                    </div>
                ),
                sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
                sorter: (() => {
                    if (!col.dataIndex) return undefined;
                    const columnSortInfo = createSortDataGetter(col.dataIndex)();
                    if (columnSortInfo.order && columnSortInfo.multiple) {
                        return {
                            compare: () => 0,
                            multiple: columnSortInfo.multiple,
                        };
                    }
                    return undefined;
                })(),
                sortOrder: (() => {
                    if (!col.dataIndex) return null;
                    const columnSortInfo = createSortDataGetter(col.dataIndex)();
                    return columnSortInfo.order ? columnSortInfo.order : null;
                })(),
                filterIcon: col.dataIndex && (
                    <span className="anticon anticon-filter">
                        <IconsMap.MoreIcon
                            height={14}
                            width={14}
                            fill="currentColor"
                        />
                    </span>
                ),
                filterDropdown: (() => {
                    if (col.dataIndex && !col?.hideSortDropdown) {
                        return (
                            <TableColumnMenu
                                setSortData={createSortDataSetter(col.dataIndex)}
                                getSortData={createSortDataGetter(col.dataIndex)}
                                withHideColumnButton={col.withHideColumnButton}
                                handleHideColumnClick={() => {}}
                            />
                        );
                    }
                    return undefined;
                })(),
            };
        }

        return col;
    });

    return {
        customTableJSX: <CustomTable
            columns={columnsWithFilters}
            {...restProps}
        />,
        tableColumnSortDataAdaptedForRequest,
        tableColumnSortData,
        lastSortedColumnData: isSingleColumnSort ? ({
            sorterField: tableColumnSortDataAdaptedForRequest[0]?.columnName,
            sorterOrder: tableColumnSortDataAdaptedForRequest[0]?.order === 'ASC' ? 'ascend' : 'descend',
        } as ISortQuery) : undefined,
    };
};
